import { AlertTriangle, Clock, Loader2, Mail, Users2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { listReferrals, pif } from '@/api/member';
import Pagination from '@/components/member/Pagination';
import PIFAction from '@/components/member/PIFAction';
import ReferralLinkBox from '@/components/member/ReferralLinkBox';

// ---------------------------------------------------------------------------
// Patch v17.5 — Referral Countdown Timer
// ---------------------------------------------------------------------------
// Reads server-provided ``expires_at`` (ISO string, UTC) plus the activity
// flags (``has_deposits``, ``has_active_subpack``, ``is_at_risk``,
// ``is_global_pool``) from the /api/me/referrals response and renders a
// live-ticking DAYS:HH:MM:SS countdown to the point at which the hourly
// referral_cleanup cron would migrate this referral into the Global PIF
// Pool.
//
// Rendering rules:
//   - If already in Global Pool → red "MIGRATED" badge (no timer).
//   - If has_deposits OR has_active_subpack → suppressed (no countdown
//     needed; they're safe).
//   - If ACTIVE / PIF-ACTIVE badge → suppressed (also safe).
//   - Otherwise → live countdown. Turns amber at <24h remaining, red at <1h.
//     At 0 shows "EXPIRING SOON" until the cron runs (up to ~1 hour after 0).
//
// Single 1-second interval per component; cheap even for a 50-row page.
const ReferralCountdown = ({ row, testid }) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!row?.expires_at) return undefined;
    if (row?.has_deposits || row?.has_active_subpack || row?.is_global_pool) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [row?.expires_at, row?.has_deposits, row?.has_active_subpack, row?.is_global_pool]);

  if (!row?.expires_at) {
    return (
      <span className="text-[10px] uppercase tracking-[0.20em] text-[var(--sc-text-mute)]" data-testid={testid}>
        —
      </span>
    );
  }
  if (row?.is_global_pool) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.18em] font-semibold text-red-300 border border-red-500/40 bg-red-500/10"
        data-testid={testid}
      >
        <AlertTriangle className="w-3 h-3" /> MIGRATED
      </span>
    );
  }
  if (row?.has_deposits || row?.has_active_subpack || row?.badge === 'ACTIVE' || row?.badge === 'PIF ACTIVE') {
    return (
      <span className="text-[10px] uppercase tracking-[0.20em] text-[var(--sc-text-mute)]" data-testid={testid}>
        secured
      </span>
    );
  }

  const expiresMs = new Date(row.expires_at).getTime();
  if (Number.isNaN(expiresMs)) {
    return (
      <span className="text-[10px] uppercase tracking-[0.20em] text-[var(--sc-text-mute)]" data-testid={testid}>
        —
      </span>
    );
  }
  const remainingMs = expiresMs - now;
  if (remainingMs <= 0) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.18em] font-semibold text-red-300 border border-red-500/40 bg-red-500/10"
        data-testid={testid}
      >
        <AlertTriangle className="w-3 h-3 animate-pulse" /> EXPIRING NOW
      </span>
    );
  }
  const totalSecs = Math.floor(remainingMs / 1000);
  const days = Math.floor(totalSecs / 86400);
  const hours = Math.floor((totalSecs % 86400) / 3600);
  const minutes = Math.floor((totalSecs % 3600) / 60);
  const seconds = totalSecs % 60;

  const hoursLeftTotal = remainingMs / (1000 * 60 * 60);
  let colorCls = 'text-[var(--sc-text-dim)] border-[rgba(255,255,255,0.08)] bg-transparent';
  if (hoursLeftTotal < 1) {
    colorCls = 'text-red-300 border-red-500/50 bg-red-500/10';
  } else if (hoursLeftTotal < 24) {
    colorCls = 'text-amber-300 border-amber-500/50 bg-amber-500/10';
  }
  const pad = (n) => String(n).padStart(2, '0');
  return (
    <span
      className={`sc-tabular inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[11px] font-medium ${colorCls}`}
      data-testid={testid}
      title={`Expires at ${new Date(row.expires_at).toLocaleString()}`}
    >
      <Clock className="w-3 h-3 shrink-0" />
      {days > 0 ? `${days}d ` : ''}
      {pad(hours)}:{pad(minutes)}:{pad(seconds)}
    </span>
  );
};

const BadgeChip = ({ kind }) => {
  if (kind === 'ACTIVE') {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-bold text-[#FFC20E] border-2 border-[#FFB612] bg-transparent"
        data-testid="badge-active"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-[#FFB612]" />
        ACTIVE
      </span>
    );
  }
  if (kind === 'PIF ACTIVE') {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-bold text-[#0a0a0b] bg-gradient-to-b from-[#FFC20E] to-[#FFB612] border border-[#FFC20E] shadow-[0_4px_16px_-4px_rgba(255,182,18,0.55)]"
        data-testid="badge-pif-active"
      >
        PIF ACTIVE
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-semibold text-[var(--sc-text-mute)] border border-[rgba(255,255,255,0.12)] bg-[rgba(255,255,255,0.02)]"
      data-testid="badge-inactive"
    >
      INACTIVE
    </span>
  );
};

const ReferralsSubpage = ({ me, onRefresh }) => {
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listReferrals(page);
      setData(r);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePIF = async (targetUserId, item, fundSource) => {
    await pif(targetUserId, item, fundSource);
    await load();
    await onRefresh?.();
  };

  return (
    <div className="space-y-6" data-testid="subpage-referrals">
      <ReferralLinkBox referralCode={me.referral_code} label="Share Your Referral Link" />

      <section className="sc-glass-strong rounded-2xl p-5 md:p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="sc-pill">
              <span className="sc-dot" />
              Pay-It-Forward Grid
            </div>
            <h3 className="font-display font-bold text-xl md:text-2xl mt-3">Your Direct Sponsorship Tree</h3>
          </div>
          <div className="hidden md:flex items-center gap-2 text-[var(--sc-text-mute)] text-[11px] uppercase tracking-[0.22em]">
            <Users2 className="w-3.5 h-3.5" />
            {data?.total ?? 0} sponsored
          </div>
        </div>

        {loading ? (
          <div className="p-8 flex items-center justify-center text-[var(--sc-text-dim)] text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
          </div>
        ) : !data?.items?.length ? (
          <div className="p-10 text-center text-[var(--sc-text-dim)] text-sm">You haven&apos;t sponsored anyone yet. Share your referral link above.</div>
        ) : (
          <div className="overflow-x-auto -mx-2 md:mx-0">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] border-b border-[rgba(255,182,18,0.10)]">
                  <th className="text-left font-medium py-3 px-3">Username</th>
                  <th className="text-left font-medium py-3 px-3 hidden md:table-cell">Email</th>
                  <th className="text-right font-medium py-3 px-3">Referrals</th>
                  <th className="text-left font-medium py-3 px-3">Status</th>
                  {/* Patch v17.5 — live countdown to 7-day Global Pool migration */}
                  <th className="text-left font-medium py-3 px-3 hidden sm:table-cell">Time Left</th>
                  <th className="text-right font-medium py-3 px-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((row) => (
                  <tr
                    key={row.id}
                    className="border-b border-[rgba(255,182,18,0.05)] hover:bg-[rgba(255,182,18,0.025)] transition"
                    data-testid={`referral-row-${row.username}`}
                  >
                    <td className="py-3 px-3">
                      <div className="font-semibold">{row.username}</div>
                      {/* Mobile-only: surface the email here since the
                          dedicated Email column is hidden on small screens.
                          Tap opens the user's preferred mail app via mailto:. */}
                      {row.email ? (
                        <a
                          href={`mailto:${row.email}`}
                          className="md:hidden inline-flex items-center gap-1 mt-1 text-[11px] text-[var(--sc-text-dim)] hover:text-[#FFC20E] active:text-[#FFC20E] transition break-all"
                          data-testid={`referral-email-mobile-${row.username}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Mail className="w-3 h-3 text-[var(--sc-text-mute)] shrink-0" />
                          <span className="underline-offset-2 hover:underline">{row.email}</span>
                        </a>
                      ) : null}
                    </td>
                    <td className="py-3 px-3 hidden md:table-cell text-[var(--sc-text-dim)]">
                      {row.email ? (
                        <a
                          href={`mailto:${row.email}`}
                          className="inline-flex items-center gap-1 hover:text-[#FFC20E] transition"
                          data-testid={`referral-email-${row.username}`}
                          onClick={(e) => e.stopPropagation()}
                          title={`Send email to ${row.email}`}
                        >
                          <Mail className="w-3 h-3 text-[var(--sc-text-mute)]" />
                          <span className="underline-offset-2 hover:underline">{row.email}</span>
                        </a>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[var(--sc-text-mute)]">
                          <Mail className="w-3 h-3" />—
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-3 sc-tabular text-right">{row.referral_count}</td>
                    <td className="py-3 px-3">
                      <BadgeChip kind={row.badge} />
                    </td>
                    <td className="py-3 px-3 hidden sm:table-cell">
                      <ReferralCountdown row={row} testid={`referral-countdown-${row.username}`} />
                    </td>
                    <td className="py-3 px-3 text-right">
                      {/* Any direct can be PIFed, active or not (Ron, 21 Oct): the API keeps an active member's badge and activation date. */}
                      <PIFAction testid={`pif-${row.username}`} onSubmit={(item, src) => handlePIF(row.id, item, src)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data && <Pagination page={data.page} totalPages={data.total_pages} total={data.total} onChange={setPage} />}
      </section>
    </div>
  );
};

export default ReferralsSubpage;
